// ===== BEGIN FILE: src/astro/probability/lifeAreaForecastEngine.ts =====
/**
 * Life Area Forecast Engine
 * Month-by-month scores (0–100) for 8 life areas, derived from
 * natal house strength, dasha lord activations, and outer-planet transits.
 */

import * as Astronomy from 'astronomy-engine';
import { getHouseLord } from '../../utils/lordshipUtils';

export interface ForecastLifeArea {
  key: string;
  label: string;
  icon: string;
  houses: number[];
  color: string;       // bar fill
  trackColor: string;  // lane background
}

export const FORECAST_LIFE_AREAS: ForecastLifeArea[] = [
  { key: 'health',    label: 'Health & Vitality',         icon: '🏥', houses: [1, 6, 8, 12],       color: '#DC2626', trackColor: '#FEF2F2' },
  { key: 'wealth',    label: 'Wealth & Finance',          icon: '💰', houses: [1, 2, 5, 9, 11, 12], color: '#059669', trackColor: '#ECFDF5' },
  { key: 'job',       label: 'Job & Career',              icon: '💼', houses: [1, 6, 10],            color: '#D97706', trackColor: '#FFFBEB' },
  { key: 'business',  label: 'Business & Enterprise',     icon: '🏢', houses: [1, 3, 7, 11],         color: '#7C3AED', trackColor: '#F5F3FF' },
  { key: 'marriage',  label: 'Marriage & Relationships',  icon: '💑', houses: [1, 7],                color: '#DB2777', trackColor: '#FDF2F8' },
  { key: 'children',  label: 'Children & Progeny',        icon: '👶', houses: [1, 5],                color: '#2563EB', trackColor: '#EFF6FF' },
  { key: 'property',  label: 'Home & Property',           icon: '🏡', houses: [1, 4],                color: '#0891B2', trackColor: '#ECFEFF' },
  { key: 'education', label: 'Education & Learning',      icon: '📚', houses: [2, 4, 5],             color: '#65A30D', trackColor: '#F7FEE7' },
];

/**
 * Natal score for a life area (0–100). Does not change month-to-month.
 * Averages the combined planetary impact + bhava bala across relevant houses.
 */
export function calcNatalScore(
  houses: number[],
  houseScores: Record<number, number>,  // 0..100 planetary impact (d1HouseInfluence.totals %)
  bhavaBala?: Record<number, number>,   // 0..100 positional strength
): number {
  let total = 0, count = 0;
  for (const h of houses) {
    const hs = houseScores[h] ?? 0;
    const planetPct = Math.max(0, Math.min(100, hs));
    const bh = bhavaBala?.[h] ?? 50;
    total += (planetPct + bh) / 2;
    count++;
  }
  return count > 0 ? Math.round(total / count) : 50;
}

/**
 * Dasha activation score for a life area for a given set of dasha lords (0–100).
 * Checks if lords occupy or rule relevant houses; penalises lords in dusthanas (6/8/12).
 */
/**
 * Returns a flat adjustment based on functional nature of the planet for the lagna.
 * yoga_karaka > benefic > neutral > malefic
 */
function funcNatureAdj(planet: string, funcNature?: Record<string, string>): number {
  const n = funcNature?.[planet] ?? 'neutral';
  if (n === 'yoga_karaka') return 12;
  if (n === 'benefic') return 6;
  if (n === 'malefic') return -10;
  return 0;
}

export function calcDashaScore(
  houses: number[],
  dashaPeriods: { MD: string; AD: string; PD: string; SD?: string },
  planets: Record<string, { house: number; sign: number }>,
  ascendantSign: number,
  funcNature?: Record<string, string>,
): number {
  const { MD, AD, PD, SD } = dashaPeriods;
  const primaryHouse = houses[0];
  const primaryLord = getHouseLord(primaryHouse as any, ascendantSign);
  let score = 50;

  // [lord, weight]: AD weighted highest; SD is most time-specific trigger
  const entries: Array<[string, number]> = [
    [MD, 1.0], [AD, 1.5], [PD, 1.0],
    ...(SD ? [[SD, 0.8] as [string, number]] : []),
  ];
  for (const [planet, wt] of entries) {
    // Functional nature modifier — proportional to dasha weight
    score += Math.round(funcNatureAdj(planet, funcNature) * wt * 0.5);
    const pd = planets[planet];
    // Is this lord the ruler of the primary house? → strong activation
    if (planet === primaryLord) score += Math.round(15 * wt);
    if (!pd) continue;
    // Occupies a relevant house → supportive
    if (houses.includes(pd.house)) score += Math.round(10 * wt);
    // In dusthana (6/8/12) → challenge
    if ([6, 8, 12].includes(pd.house)) score -= Math.round(8 * wt);
  }

  return Math.max(0, Math.min(100, Math.round(score)));
}

/**
 * Transit score for a life area for a given calendar date (0–100).
 * Uses Jupiter (expansion/benefit) and Saturn (restriction/delay) transits.
 */
export function calcTransitScore(
  houses: number[],
  date: Date,
  ascendantSign: number,
  ayanamsa: number = 24.14,
  funcNature?: Record<string, string>,
): number {
  try {
    const time = Astronomy.MakeTime(date);
    const primaryHouse = houses[0];
    let score = 50;

    const toSidLon = (body: Astronomy.Body): number => {
      const vec = Astronomy.GeoVector(body, time, true);
      const ecl = Astronomy.Ecliptic(vec);
      return ((ecl.elon - ayanamsa) % 360 + 360) % 360;
    };
    const toHouse = (lon: number): number => {
      const sign = Math.floor(lon / 30) + 1;
      return ((sign - ascendantSign + 12) % 12) + 1;
    };

    // Mean lunar node (Rahu) via IAU formula — same as astroEngine.ts
    const rahuTropLon = (() => {
      const T = (date.getTime() / 86400000 - 10957.5) / 36525;
      const deg = 125.04455501 - 1934.1361849 * T + 0.0020762 * T * T;
      return ((deg % 360) + 360) % 360;
    })();
    const rahuSidLon = ((rahuTropLon - ayanamsa) % 360 + 360) % 360;
    const ketuSidLon = (rahuSidLon + 180) % 360;

    const jupHouse  = toHouse(toSidLon(Astronomy.Body.Jupiter));
    const satHouse  = toHouse(toSidLon(Astronomy.Body.Saturn));
    const marsHouse = toHouse(toSidLon(Astronomy.Body.Mars));
    const rahuHouse = toHouse(rahuSidLon);
    const ketuHouse = toHouse(ketuSidLon);

    const jupNature  = funcNature?.['Jupiter'] ?? 'neutral';
    const satNature  = funcNature?.['Saturn']  ?? 'neutral';
    const marsNature = funcNature?.['Mars']    ?? 'neutral';
    const rahuNature = funcNature?.['Rahu']    ?? 'neutral';
    const ketuNature = funcNature?.['Ketu']    ?? 'neutral';

    // Jupiter: natural benefic — can be FM for lagnas where it rules 6th/8th/12th
    const jupPrimary = jupNature === 'malefic' ? -12 :
                       jupNature === 'yoga_karaka' ? 25 : 20;
    // Saturn: natural malefic — Yoga Karaka for Libra/Taurus lagna
    const satPrimary = satNature === 'yoga_karaka' ? 15 :
                       satNature === 'benefic'     ? 5  : -15;
    // Mars: natural malefic — Yoga Karaka for Cancer/Leo lagna
    const marsPrimary = marsNature === 'yoga_karaka' ? 12 :
                        marsNature === 'benefic'     ? 5  : -10;
    // Rahu: generally turbulent, can be beneficial if lord of kendras
    const rahuPrimary = rahuNature === 'benefic' || rahuNature === 'yoga_karaka' ? 8 : -10;
    // Ketu: moksha karaka, disruptive in material houses
    const ketuPrimary = ketuNature === 'benefic' || ketuNature === 'yoga_karaka' ? 6 : -8;

    const applyTransit = (house: number, primary: number) => {
      if (house === primaryHouse) score += primary;
      else if (houses.includes(house)) score += Math.round(primary * 0.5);
    };

    applyTransit(jupHouse,  jupPrimary);
    applyTransit(satHouse,  satPrimary);
    applyTransit(marsHouse, marsPrimary);
    applyTransit(rahuHouse, rahuPrimary);
    applyTransit(ketuHouse, ketuPrimary);

    return Math.max(0, Math.min(100, Math.round(score)));
  } catch {
    return 50; // neutral fallback if astronomy calc fails
  }
}

/** Background / text / fill colours for a score value */
export function getScoreColors(score: number): { bg: string; text: string; fill: string } {
  if (score >= 70) return { bg: '#DCFCE7', text: '#166534', fill: '#22C55E' };
  if (score >= 55) return { bg: '#FEF9C3', text: '#713F12', fill: '#EAB308' };
  if (score >= 40) return { bg: '#FED7AA', text: '#7C2D12', fill: '#F97316' };
  return { bg: '#FEE2E2', text: '#7F1D1D', fill: '#EF4444' };
}

/** Short planet abbreviations for dasha row labels */
export const PLANET_ABBR: Record<string, string> = {
  Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me', Jupiter: 'Ju',
  Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke',
};
// ===== END FILE: src/astro/probability/lifeAreaForecastEngine.ts =====
